import { useMemo } from "react";
import { z } from "zod";

import { isBlankValue } from "hooks/useCustomTableRowMutations";

const REQUIRED = "Required.";

const coerceNumber = v => {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : NaN;
};

// Percentage/currency accept their decorated input forms ("85%",
// "$1,234.56") — strip the decoration before numeric coercion, mirroring
// the backend coercers' leniency for these two types.
const coerceDecoratedNumber = v => {
  if (typeof v !== "string") return coerceNumber(v);
  const stripped = v.replace(/[%$,]/g, "").trim();
  return coerceNumber(stripped);
};

// Mirror of the backend is_valid_url regex (validation_utils.py) so manual
// entry and CSV import agree: an http(s):// scheme is optional, and bare hosts
// ("google.com"), subdomains, localhost, and IPv4 all validate. A non-http(s)
// scheme (ftp://) or a bare word with no TLD still fails.
const URL_RE = new RegExp(
  "^((?:http)s?://)?" +
    "(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:[a-z]{2,6}\\.?|[a-z0-9-]{2,}\\.?)|" +
    "localhost|" +
    "\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3})" +
    "(?::\\d+)?" +
    "(?:/?|[/?]\\S+)$",
  "i"
);
const isUrl = v => URL_RE.test(v.trim());

// Pragmatic mirror of the backend's phonenumbers "possible number" check:
// optional +, digits with common separators, 7–15 digits total (E.164 max).
const isPhoneLike = v => {
  if (/[^\d\s()+.\-]/.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

// Mirror of the backend is_valid_email regex (validation_utils.py) so manual
// entry and CSV import agree: a dot-atom local part whose first character is
// alphanumeric (a leading sign like "+jon@" fails; an internal sign as in
// "jon+sub@" is fine) and a dotted host with an alphabetic TLD. Numeric TLDs
// ("x@y.123") and bare hostnames fail. Zod's own .email() disagrees on these
// edges, so refine against the backend rule instead.
const EMAIL_RE =
  /^[A-Za-z0-9][A-Za-z0-9!#$%&'*+/=?^_`{|}~-]*(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
const isEmail = v => EMAIL_RE.test(v.trim());

const baseFieldForType = type => {
  switch (type) {
    case "short_text":
      // Mirrors the backend VARCHAR(512) cap so an indexed value can never
      // exceed Postgres's btree entry limit.
      return z.string().max(512).nullable();
    case "text":
      return z.string().nullable();
    case "number":
      return z.preprocess(coerceNumber, z.number().nullable());
    case "boolean":
      return z.boolean();
    case "date":
      return z.string().nullable();
    case "single_select":
      return z.string().nullable();
    case "multi_select":
    case "list":
      return z.array(z.string()).default([]);
    case "relationship":
      // Raw record id from the picker; existence/liveness is server-side.
      return z.number().int().positive().nullable();
    case "email":
      return z
        .string()
        .max(512)
        .refine(isEmail, "Enter a valid email address.")
        .nullable()
        .or(z.literal("").transform(() => null));
    case "url":
      return z
        .string()
        .max(512)
        .refine(isUrl, "Enter a valid URL.")
        .nullable()
        .or(z.literal("").transform(() => null));
    case "phone":
      return z
        .string()
        .refine(isPhoneLike, "Enter a valid phone number.")
        .nullable()
        .or(z.literal("").transform(() => null));
    case "percentage":
    case "currency":
      return z.preprocess(coerceDecoratedNumber, z.number().nullable());
    default:
      return z.unknown();
  }
};

const requiredRefine = (field, type) => {
  if (type === "boolean") return field;
  if (type === "number" || type === "relationship") {
    return field.refine(v => v != null, REQUIRED);
  }
  if (type === "date") return field.refine(v => !!v, REQUIRED);
  if (type === "multi_select" || type === "list") {
    return field.refine(v => Array.isArray(v) && v.length > 0, REQUIRED);
  }
  return field.refine(v => !isBlankValue(v, type), REQUIRED);
};

const buildSchema = schema => {
  const columns = schema?.columns;
  if (!Array.isArray(columns) || columns.length === 0) return z.object({});
  const shape = {};
  for (const col of columns) {
    let field = baseFieldForType(col.type);
    if (col.is_required) field = requiredRefine(field, col.type);
    shape[col.field_id] = field;
  }
  return z.object(shape);
};

/**
 * Build a Zod schema from a custom-table schema descriptor. Memoized on the
 * schema reference so the resolver doesn't thrash on every render.
 *
 * Server-only validations (uniqueness, foreign keys) are not enforced here —
 * the API returns 422 and `useFormErrors` maps it to per-field errors.
 */
export const useDynamicZodSchema = schema =>
  useMemo(() => buildSchema(schema), [schema]);

export default useDynamicZodSchema;
